import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { FilebrokennotfoundIllustration } from "vega-illustrations";

import { type TaskResponse } from "@vega-api/generated/types/taskResponse";
import { ActionStatusMessage } from "@vega-core/components/Deprecated/ActionStatusMessage/ActionStatusMessage";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { HorizontalStepIndicator } from "@vega-core/components/Navigation/Steppers/HorizontalStepIndicator";
import { NoticeType } from "@vega-core/components/Notice/Notice";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { useTasksStateContext } from "@vega-modules/context/TasksStateContext/context";
import { CommonPaths } from "@vega-modules/paths";
import { type Tile } from "@vega-modules/scenes/CoInvestmentsLP/components/Welcome/WelcomeStepsTiles";

import { AccreditedInvestorStatus } from "./steps/AccreditedInvestorStatus";
import { TermsAndConditionsV2 } from "./steps/TermsAndConditions/TermsAndConditionsV2";
import { WelcomeStep } from "./steps/WelcomeStep";
import { TaskTypes } from "./constants";
import { WelcomeHeader } from "./WelcomeHeader";

type Props = {
  welcomeStepHeading?: string;
  welcomeStepTiles?: Tile[];
};

export const WelcomeTasksPageV1 = ({
  welcomeStepHeading,
  welcomeStepTiles,
}: Props) => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const isMobile = !useDeviceWidth("sm");

  const tasksState = useTasksStateContext();

  if (tasksState.version !== "v1") {
    throw new Error("WelcomeTasksPageV1 requires the v1 tasks state");
  }

  const {
    pendingTasks,
    hasPendingTasks,
    isLoading,
    isCompleted,
    isError,
    refetch,
  } = tasksState;

  const steps = useMemo(
    () =>
      pendingTasks
        .map((task) => task.type)
        .filter((item): item is string => isTruthy(item)),
    [pendingTasks],
  );

  const handleNextStep = useCallback(() => {
    if (activeStep < steps.length - 1) {
      setActiveStep((step) => step + 1);

      return;
    }

    refetch();
  }, [activeStep, refetch, steps.length]);

  const getStepComponentByType = useCallback(
    (taskType: TaskResponse["type"]) => {
      const task = pendingTasks.find((task) => task.type === taskType);

      if (task?.type === TaskTypes.WELCOME) {
        return (
          <WelcomeStep
            heading={welcomeStepHeading}
            onNextStep={handleNextStep}
            task={task}
            tiles={welcomeStepTiles}
          />
        );
      }

      if (task?.type === TaskTypes.AI_CONFIRMATION) {
        return (
          <AccreditedInvestorStatus onNextStep={handleNextStep} task={task} />
        );
      }

      if (task?.type === TaskTypes.TERMS_AND_CONDITIONS) {
        return <TermsAndConditionsV2 onNextStep={handleNextStep} task={task} />;
      }

      return null;
    },
    [handleNextStep, pendingTasks, welcomeStepHeading, welcomeStepTiles],
  );

  const currentStep = steps[activeStep];

  useEffect(
    function redirectOnCompletion() {
      if (isCompleted) {
        navigate(CommonPaths.Home);
      }
    },
    [isCompleted, navigate],
  );

  if (isError && !hasPendingTasks) {
    return (
      <div className="flex flex-col min-h-dvh bg-background/secondary text-text/primary">
        <WelcomeHeader />
        <main className="flex-1 flex flex-col gap-4 items-center justify-center">
          <FilebrokennotfoundIllustration />
          <ActionStatusMessage
            contactLabel={(m.titles_contactUs() as string).toLowerCase()}
            message={m.errors_unexpectedErrorChunk1()}
            type={NoticeType.ERROR}
            withContact
          />
        </main>
      </div>
    );
  }

  const getStepComponent = () => {
    if (isLoading) return <LoadingSpinner />;

    if (isTruthy(currentStep)) return getStepComponentByType(currentStep);
  };

  return (
    <div
      className={cnTwMerge(
        "flex flex-col min-h-dvh bg-background/secondary text-text/primary",
        isMobile && "h-dvh overflow-hidden",
      )}
    >
      <WelcomeHeader />

      <main
        className={cnTwMerge(
          "flex-1 flex items-center justify-center text-center",
          isMobile && "min-h-0 overflow-y-auto items-start",
        )}
      >
        {getStepComponent()}
      </main>

      <footer
        className={cnTwMerge(
          "h-13 flex items-center justify-center mt-14",
          isMobile && "mt-0",
        )}
      >
        <div className="w-[480px]">
          {steps.length > 1 ? (
            <HorizontalStepIndicator activeStep={activeStep} steps={steps} />
          ) : null}
        </div>
      </footer>
    </div>
  );
};
